/** @file Resets local/applied/slot/ik buffers of a pose to the setup pose. */
import { POSE_LAYOUT, type Pose } from '../contracts/pose';
import type { SkeletonData, SkinData } from '../skeletonData';

const L = POSE_LAYOUT;
/** LOCAL_STRIDE offset of each BoneData.setup entry, in setup order [x, y, rotation, scaleX, scaleY, shearX, shearY]. */
const SETUP_OFFSETS = [L.X, L.Y, L.ROTATION, L.SCALE_X, L.SCALE_Y, L.SHEAR_X, L.SHEAR_Y] as const;

/** Bone setup into local and applied through the POSE_LAYOUT offsets, setup inherit codes. */
function resetBones(pose: Pose, data: SkeletonData): void {
  for (let i = 0; i < data.bones.length; i++) {
    const bone = data.bones[i];
    if (bone === undefined) continue;
    const base = i * L.LOCAL_STRIDE;
    for (let k = 0; k < SETUP_OFFSETS.length; k++) {
      const o = base + (SETUP_OFFSETS[k] ?? 0);
      const v = bone.setup[k] ?? 0;
      pose.local[o] = v;
      pose.applied[o] = v;
    }
    pose.inherit[i] = bone.inherit;
  }
}

/** Slot colours, setup draw order (slot order) and setup attachments resolved through `skin` (-1 = none). */
function resetSlots(pose: Pose, data: SkeletonData, skin: SkinData): void {
  for (let i = 0; i < data.slots.length; i++) {
    const slot = data.slots[i];
    if (slot === undefined) continue;
    pose.slotColor[i] = slot.color;
    pose.slotDark[i] = slot.dark;
    pose.slotAttachment[i] = slot.attachment < 0 ? -1 : (skin.map.get(i)?.get(slot.attachment) ?? -1);
    pose.drawOrder[i] = i;
  }
}

/** IK setup channels: mix, softness, bend (1 = bendPositive), stretch and compress as 0/1. */
function resetIk(pose: Pose, data: SkeletonData): void {
  const { IK_STRIDE, IK_MIX, IK_SOFTNESS, IK_BEND, IK_STRETCH, IK_COMPRESS } = POSE_LAYOUT;
  for (let i = 0; i < data.ik.length; i++) {
    const ik = data.ik[i];
    if (ik === undefined) continue;
    const o = i * IK_STRIDE;
    pose.ik[o + IK_MIX] = ik.mix;
    pose.ik[o + IK_SOFTNESS] = ik.softness;
    pose.ik[o + IK_BEND] = ik.bendPositive ? 1 : 0;
    pose.ik[o + IK_STRETCH] = ik.stretch ? 1 : 0;
    pose.ik[o + IK_COMPRESS] = ik.compress ? 1 : 0;
  }
}

/**
 * Copies setup values into local, applied, slot (attachments resolved through `skin`), ik and deform buffers; leaves
 * world and mounts untouched. Also restores setup inherit codes and the setup draw order; never touches
 * slotBoneOverride, slotMount, slotMountActive or flipX (runtime equip and directions own them). Allocation-free.
 */
export function resetPose(pose: Pose, data: SkeletonData, skin: SkinData): void {
  resetBones(pose, data);
  resetSlots(pose, data, skin);
  resetIk(pose, data);
  for (let i = 0; i < pose.deform.length; i++) pose.deform[i]?.fill(0);
}
